import { createFileRoute, Link } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminAmdLiveSummary,
    useAdminAmdShadowSummary,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AmdLiveCall,
    type AmdShadowCall,
    type AmdShadowCandidateSummary,
} from '@libraries/typescript/api_client/schema_types';

export const Route = createFileRoute('/_app/admin/amd-shadow')({
    component: AmdShadowPage,
    head: () => ({
        meta: [
            {
                title: 'AMD Shadow | Phoebe Admin',
            },
        ],
    }),
});

const DAY_OPTIONS = [1, 7, 14, 30] as const;

function recallLabel(caught: number, total: number): string {
    if (total === 0) {
        return '—';
    }
    return `${((caught / total) * 100).toFixed(0)}%`;
}

function rateLabel(numerator: number, denominator: number): string {
    if (denominator === 0) {
        return '—';
    }
    return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

function StatTile({
    label,
    value,
    hint,
}: {
    readonly label: string;
    readonly value: string;
    readonly hint?: string;
}) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-muted-foreground text-sm font-medium">
                    {label}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="text-3xl font-semibold">{value}</div>
                {hint ? (
                    <div className="text-muted-foreground mt-1 text-xs">
                        {hint}
                    </div>
                ) : null}
            </CardContent>
        </Card>
    );
}

function PredictionBadge({ predicted }: { readonly predicted: string }) {
    return (
        <Badge variant={predicted === 'machine' ? 'secondary' : 'outline'}>
            {predicted === 'machine' ? 'voicemail' : 'human'}
        </Badge>
    );
}

function DisagreementRow({ call }: { readonly call: AmdShadowCall }) {
    return (
        <TableRow>
            <TableCell className="text-muted-foreground whitespace-nowrap">
                {call.created_at
                    ? new Date(call.created_at).toLocaleString()
                    : '—'}
            </TableCell>
            <TableCell>{call.organization_name}</TableCell>
            <TableCell>
                <PredictionBadge predicted={call.predicted} />
            </TableCell>
            <TableCell>
                {call.final_voicemail_detected ? 'voicemail' : 'not voicemail'}
            </TableCell>
            <TableCell className="tabular-nums">
                {call.score !== null && call.score !== undefined
                    ? call.score.toFixed(3)
                    : '—'}
            </TableCell>
            <TableCell>
                <Link
                    className="text-primary underline-offset-2 hover:underline"
                    params={{ callId: call.voice_call_id }}
                    to="/admin/voice-qa/$callId"
                >
                    Open call
                </Link>
            </TableCell>
        </TableRow>
    );
}

function LiveDisagreementRow({ call }: { readonly call: AmdLiveCall }) {
    return (
        <TableRow>
            <TableCell>{call.organization_name}</TableCell>
            <TableCell className="text-muted-foreground whitespace-nowrap">
                {call.created_at
                    ? new Date(call.created_at).toLocaleString()
                    : '—'}
            </TableCell>
            <TableCell className="tabular-nums">
                {call.score !== null && call.score !== undefined
                    ? call.score.toFixed(3)
                    : '—'}
            </TableCell>
            <TableCell>{call.pivoted ? 'Yes' : 'No'}</TableCell>
            <TableCell>
                {call.llm_marked_voicemail ? 'voicemail' : '—'}
            </TableCell>
            <TableCell>
                <Link
                    className="text-primary underline-offset-2 hover:underline"
                    params={{ callId: call.voice_call_id }}
                    to="/admin/voice-qa/$callId"
                >
                    Open call
                </Link>
            </TableCell>
        </TableRow>
    );
}

function CandidateSummaryRow({
    candidate,
}: {
    readonly candidate: AmdShadowCandidateSummary;
}) {
    const finalVoicemailTotal =
        candidate.predicted_machine_final_true +
        candidate.predicted_human_final_true;
    const finalHumanTotal =
        candidate.predicted_machine_final_false +
        candidate.predicted_human_final_false;

    return (
        <TableRow>
            <TableCell className="font-medium">{candidate.name}</TableCell>
            <TableCell className="tabular-nums">{candidate.total}</TableCell>
            <TableCell className="tabular-nums">
                {candidate.agreement_rate !== null &&
                candidate.agreement_rate !== undefined
                    ? `${(candidate.agreement_rate * 100).toFixed(1)}%`
                    : '—'}
            </TableCell>
            <TableCell className="tabular-nums">
                {rateLabel(
                    candidate.predicted_machine_final_false,
                    finalHumanTotal,
                )}
            </TableCell>
            <TableCell className="tabular-nums">
                {rateLabel(
                    candidate.predicted_machine_final_true,
                    finalVoicemailTotal,
                )}
            </TableCell>
            <TableCell className="tabular-nums">
                {candidate.predicted_machine_final_false}
            </TableCell>
            <TableCell className="tabular-nums">
                {candidate.predicted_human_final_true}
            </TableCell>
        </TableRow>
    );
}

function AmdShadowPage() {
    const [days, setDays] = useState<number>(7);
    const { data: summary, isLoading, error } = useAdminAmdShadowSummary(days);
    const {
        data: liveSummary,
        isLoading: isLiveLoading,
        error: liveError,
    } = useAdminAmdLiveSummary(days);

    return (
        <div className="mx-auto flex max-w-5xl flex-col gap-6 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">
                        AMD Shadow Comparison
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Audio voicemail-detection model vs the final
                        voicemail_detected outcome, on shadow-classified calls
                        (PHO-13571). No call behavior is affected.
                    </p>
                </div>
                <div className="flex gap-1">
                    {DAY_OPTIONS.map((option) => (
                        <Button
                            key={option}
                            onClick={() => setDays(option)}
                            size="sm"
                            variant={days === option ? 'default' : 'outline'}
                        >
                            {option}d
                        </Button>
                    ))}
                </div>
            </div>
            <div className="flex flex-col gap-6">
                <div>
                    <h2 className="text-xl font-semibold">
                        Live AMD (ring rollout)
                    </h2>
                    <p className="text-muted-foreground text-sm">
                        In-call verdicts on ringed orgs (PHO-13739). Machine
                        verdicts pivot the agent at ~5s.
                    </p>
                </div>
                {isLiveLoading ? (
                    <div className="text-muted-foreground flex items-center gap-2">
                        <Spinner size="md" tone="muted" />
                        Loading live AMD results…
                    </div>
                ) : null}
                {liveError ? (
                    <div className="text-destructive text-sm">
                        Failed to load live AMD results.
                    </div>
                ) : null}
                {liveSummary && liveSummary.total === 0 ? (
                    <p className="text-muted-foreground text-sm">
                        No probed calls in this window — is the ring flag on?
                    </p>
                ) : null}
                {liveSummary && liveSummary.total > 0 ? (
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                            <StatTile
                                label="Probed calls"
                                value={String(liveSummary.total)}
                                hint={
                                    liveSummary.pending_outcome_count > 0
                                        ? `last ${liveSummary.days}d · ${liveSummary.pending_outcome_count} outcomes pending`
                                        : `last ${liveSummary.days}d`
                                }
                            />
                            <StatTile
                                label="Pivot rate"
                                value={`${(
                                    (liveSummary.pivot_count /
                                        liveSummary.total) *
                                    100
                                ).toFixed(1)}%`}
                                hint={`${liveSummary.pivot_count} of ${liveSummary.total}`}
                            />
                            <StatTile
                                label="Machine but live"
                                value={String(
                                    liveSummary.machine_but_live.length,
                                )}
                                hint="possible false pivots — listen"
                            />
                            <StatTile
                                label="Human but voicemail"
                                value={String(
                                    liveSummary.human_but_voicemail.length,
                                )}
                                hint="AMD missed; tool caught"
                            />
                            <StatTile
                                label="AMD caught live"
                                value={recallLabel(
                                    liveSummary.amd_caught_live_count,
                                    liveSummary.voicemail_outcome_count,
                                )}
                                hint={`${liveSummary.amd_caught_live_count} of ${liveSummary.voicemail_outcome_count} voicemails, in-call`}
                            />
                            <StatTile
                                label="LLM caught live"
                                value={recallLabel(
                                    liveSummary.llm_caught_live_count,
                                    liveSummary.voicemail_outcome_count,
                                )}
                                hint={`${liveSummary.llm_caught_live_count} of ${liveSummary.voicemail_outcome_count} · agent alone (mark_as_voicemail)`}
                            />
                        </div>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Machine but live (
                                    {liveSummary.machine_but_live.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {liveSummary.machine_but_live.length === 0 ? (
                                    <p className="text-muted-foreground text-sm">
                                        No machine-but-live calls in this
                                        window.
                                    </p>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Organization
                                                </TableHead>
                                                <TableHead>Time</TableHead>
                                                <TableHead>Score</TableHead>
                                                <TableHead>Pivoted</TableHead>
                                                <TableHead>LLM live</TableHead>
                                                <TableHead />
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {liveSummary.machine_but_live.map(
                                                (call) => (
                                                    <LiveDisagreementRow
                                                        call={call}
                                                        key={call.voice_call_id}
                                                    />
                                                ),
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Human but voicemail (
                                    {liveSummary.human_but_voicemail.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {liveSummary.human_but_voicemail.length ===
                                0 ? (
                                    <p className="text-muted-foreground text-sm">
                                        No human-but-voicemail calls in this
                                        window.
                                    </p>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Organization
                                                </TableHead>
                                                <TableHead>Time</TableHead>
                                                <TableHead>Score</TableHead>
                                                <TableHead>Pivoted</TableHead>
                                                <TableHead>LLM live</TableHead>
                                                <TableHead />
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {liveSummary.human_but_voicemail.map(
                                                (call) => (
                                                    <LiveDisagreementRow
                                                        call={call}
                                                        key={call.voice_call_id}
                                                    />
                                                ),
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                ) : null}
            </div>
            {isLoading ? (
                <div className="text-muted-foreground flex items-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    Loading shadow results…
                </div>
            ) : null}
            {error ? (
                <div className="text-destructive text-sm">
                    Failed to load AMD shadow results.
                </div>
            ) : null}
            {summary ? (
                <div className="flex flex-col gap-6">
                    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                        <StatTile
                            label="Classified calls"
                            value={String(summary.total)}
                            hint={`last ${summary.days}d`}
                        />
                        <StatTile
                            label="Agreement rate"
                            value={
                                summary.agreement_rate !== null &&
                                summary.agreement_rate !== undefined
                                    ? `${(summary.agreement_rate * 100).toFixed(1)}%`
                                    : '—'
                            }
                            hint="model matches final outcome"
                        />
                        <StatTile
                            label="Model missed voicemail"
                            value={String(summary.predicted_human_final_true)}
                            hint="said human, final says voicemail"
                        />
                        <StatTile
                            label="Model false voicemail"
                            value={String(
                                summary.predicted_machine_final_false,
                            )}
                            hint="said voicemail, final says human"
                        />
                    </div>
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                Confusion matrix
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Table className="max-w-xl">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead />
                                        <TableHead>Model: voicemail</TableHead>
                                        <TableHead>Model: human</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    <TableRow>
                                        <TableCell className="font-medium">
                                            Final: voicemail
                                        </TableCell>
                                        <TableCell className="tabular-nums">
                                            {
                                                summary.predicted_machine_final_true
                                            }
                                        </TableCell>
                                        <TableCell
                                            className={cn(
                                                'tabular-nums',
                                                summary.predicted_human_final_true >
                                                    0 && 'font-semibold',
                                            )}
                                        >
                                            {summary.predicted_human_final_true}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell className="font-medium">
                                            Final: not voicemail
                                        </TableCell>
                                        <TableCell
                                            className={cn(
                                                'tabular-nums',
                                                summary.predicted_machine_final_false >
                                                    0 && 'font-semibold',
                                            )}
                                        >
                                            {
                                                summary.predicted_machine_final_false
                                            }
                                        </TableCell>
                                        <TableCell className="tabular-nums">
                                            {
                                                summary.predicted_human_final_false
                                            }
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                            <p className="text-muted-foreground mt-2 text-xs">
                                Off-diagonal cells are disagreements. The final
                                outcome comes from mark_as_voicemail + the
                                transcript classifier, which remain the source
                                of truth.
                            </p>
                        </CardContent>
                    </Card>
                    {summary.candidate_summaries.length > 0 ? (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-base">
                                    Shadow candidates
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Candidate</TableHead>
                                            <TableHead>Total</TableHead>
                                            <TableHead>Agreement</TableHead>
                                            <TableHead>
                                                False voicemail
                                            </TableHead>
                                            <TableHead>
                                                Voicemail recall
                                            </TableHead>
                                            <TableHead>FP</TableHead>
                                            <TableHead>FN</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {summary.candidate_summaries.map(
                                            (candidate) => (
                                                <CandidateSummaryRow
                                                    candidate={candidate}
                                                    key={candidate.name}
                                                />
                                            ),
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    ) : null}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">
                                Disagreements ({summary.disagreements.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {summary.disagreements.length === 0 ? (
                                <p className="text-muted-foreground text-sm">
                                    No disagreements in the selected window.
                                </p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Time</TableHead>
                                            <TableHead>Organization</TableHead>
                                            <TableHead>Model said</TableHead>
                                            <TableHead>Final outcome</TableHead>
                                            <TableHead>Score</TableHead>
                                            <TableHead />
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {summary.disagreements.map((call) => (
                                            <DisagreementRow
                                                call={call}
                                                key={call.voice_call_id}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </div>
            ) : null}
        </div>
    );
}
